import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Copy,
  Inbox,
  Mail,
  RotateCcw,
  Search,
  Ticket,
  Trash2,
  XCircle,
} from 'lucide-react';
import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { SelectNativo } from '@/components/ui/select-nativo';
import { confirmar } from '@/components/ui/confirm-dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { cn } from '@/lib/utils';
import { solicitudesService } from '../services/solicitudes.service';
import {
  rotuloDeAsunto,
  rotuloDeRubro,
  rotuloDeTamano,
  type EstadoDeSolicitud,
  type FichaDeSolicitud,
  type InvitacionDeLaSolicitud,
  type ListaDeSolicitudes,
} from '../types/solicitudes.types';

/*
  Plataforma › Solicitudes (vault 346, §5.5). Lo que llega desde las páginas públicas: pedidos de Mini
  Prixia y mensajes de Contacto (D8). Se revisan, se descartan con motivo o se invita: la invitación es de
  una sola empresa y vence a los 14 días (D10). El correo se manda a mano: el panel lo deja armado.
*/

export const ESTADOS_DE_SOLICITUD: Record<
  EstadoDeSolicitud,
  {
    rotulo: string;
    variante: 'success' | 'secondary' | 'warning' | 'info' | 'outline';
    clase?: string;
  }
> = {
  NUEVA: { rotulo: 'Nueva', variante: 'warning' },
  EN_REVISION: { rotulo: 'En revisión', variante: 'info' },
  INVITADA: {
    rotulo: 'Invitada',
    variante: 'outline',
    clase: 'border-plataforma/40 bg-plataforma-suave text-plataforma',
  },
  ACTIVADA: { rotulo: 'Activada', variante: 'success' },
  DESCARTADA: { rotulo: 'Descartada', variante: 'secondary' },
};

const FILTROS: Array<{ valor: EstadoDeSolicitud | ''; rotulo: string }> = [
  { valor: '', rotulo: 'Todas' },
  { valor: 'NUEVA', rotulo: 'Nuevas' },
  { valor: 'EN_REVISION', rotulo: 'En revisión' },
  { valor: 'INVITADA', rotulo: 'Invitadas' },
  { valor: 'ACTIVADA', rotulo: 'Activadas' },
  { valor: 'DESCARTADA', rotulo: 'Descartadas' },
];

const esEstado = (v: string | null): v is EstadoDeSolicitud =>
  Boolean(v && v in ESTADOS_DE_SOLICITUD);

const fecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-BO', { day: 'numeric', month: 'short', year: 'numeric' });

function PastillaDeEstado({ estado }: { estado: EstadoDeSolicitud }) {
  const e = ESTADOS_DE_SOLICITUD[estado];
  return (
    <Badge variant={e.variante} className={e.clase}>
      {e.rotulo}
    </Badge>
  );
}

export function SolicitudesPage() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const estado = esEstado(params.get('estado')) ? (params.get('estado') as EstadoDeSolicitud) : '';
  const [buscar, setBuscar] = useState('');
  const [buscando, setBuscando] = useState('');
  const [pagina, setPagina] = useState(1);
  const [lista, setLista] = useState<ListaDeSolicitudes | null>(null);
  const [errorAlCargar, setErrorAlCargar] = useState(false);
  const [intento, setIntento] = useState(0);
  const [abierta, setAbierta] = useState<string | null>(null);

  const cargar = useCallback(() => {
    return solicitudesService
      .listar({ estado, buscar: buscando, pagina })
      .then((data) => {
        setLista(data);
        setErrorAlCargar(false);
      })
      .catch(() => setErrorAlCargar(true));
  }, [estado, buscando, pagina]);

  useEffect(() => {
    void cargar();
  }, [cargar, intento]);

  const cambiarFiltro = (valor: string) => {
    setPagina(1);
    const siguientes = new URLSearchParams(params);
    if (valor) siguientes.set('estado', valor);
    else siguientes.delete('estado');
    setParams(siguientes, { replace: true });
  };

  const paginas = lista ? Math.max(1, Math.ceil(lista.total / lista.porPagina)) : 1;

  return (
    <AppLayout variante="plataforma">
      <div className="space-y-6 p-6">
        <div className="flex items-start gap-3">
          <Inbox className="mt-1 h-6 w-6" aria-hidden="true" />
          <div>
            <div className="mb-3">
              <EtiquetaDePagina tono="neutro">Plataforma</EtiquetaDePagina>
            </div>
            <h1 className={CLASE_TITULO_DE_PAGINA}>Solicitudes</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Lo que llega desde las páginas públicas: pedidos de Mini Prixia y mensajes de
              Contacto. Revísalas, crea la invitación y mándala tú.
              {lista && lista.nuevas > 0 ? ` Hay ${lista.nuevas} sin revisar.` : ''}
            </p>
          </div>
        </div>

        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setPagina(1);
            setBuscando(buscar);
          }}
        >
          <SelectNativo
            aria-label="Filtrar por estado"
            value={estado}
            onChange={(e) => cambiarFiltro(e.target.value)}
          >
            {FILTROS.map((f) => (
              <option key={f.valor} value={f.valor}>
                {f.rotulo}
              </option>
            ))}
          </SelectNativo>
          <Input
            aria-label="Buscar por persona, correo o empresa"
            placeholder="Persona, correo o empresa"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            className="w-64"
          />
          <Button type="submit" variant="outline">
            <Search className="mr-2 h-4 w-4" aria-hidden="true" />
            Buscar
          </Button>
        </form>

        <Card>
          <CardContent className="p-0">
            {errorAlCargar ? (
              <div className="flex flex-col items-center gap-3 p-10 text-center">
                <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
                <div>
                  <p className="font-medium">No se pudieron cargar las solicitudes</p>
                  <p className="text-sm text-muted-foreground">
                    Puede ser un problema de conexión. Prueba de nuevo.
                  </p>
                </div>
                <Button variant="outline" onClick={() => setIntento((n) => n + 1)}>
                  Reintentar
                </Button>
              </div>
            ) : !lista ? (
              <div className="space-y-3 p-6">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : lista.filas.length === 0 ? (
              <p className="p-10 text-center text-sm text-muted-foreground">
                No hay solicitudes con este filtro.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Persona</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Rubro y catálogo</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lista.filas.map((s) => (
                      <TableRow
                        key={s.id}
                        className="cursor-pointer"
                        onClick={() => setAbierta(s.id)}
                      >
                        <TableCell className="whitespace-nowrap text-sm">
                          {fecha(s.createdAt)}
                        </TableCell>
                        <TableCell>
                          <button
                            type="button"
                            className="text-left font-medium hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAbierta(s.id);
                            }}
                          >
                            {s.datosBorrados ? 'Datos borrados' : s.nombre}
                          </button>
                          {s.correo && (
                            <div className="text-xs text-muted-foreground">
                              {s.correo}
                              {s.correoPersonal && (
                                <Badge variant="outline" className="ml-2">
                                  Correo personal
                                </Badge>
                              )}
                            </div>
                          )}
                          {s.vecesPedida > 1 && (
                            <div className="text-xs text-muted-foreground">
                              Pidió {s.vecesPedida} veces
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {s.empresa ?? '—'}
                          {s.asunto && s.asunto !== 'demo-producto' && (
                            <div className="text-xs text-muted-foreground">
                              Contacto · {rotuloDeAsunto(s.asunto)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {rotuloDeRubro(s.rubro) ?? '—'}
                          {s.tamanoDeCatalogo && (
                            <div className="text-xs text-muted-foreground">
                              {rotuloDeTamano(s.tamanoDeCatalogo)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <PastillaDeEstado estado={s.estado} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {lista && paginas > 1 && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <Button
              variant="outline"
              size="sm"
              disabled={pagina <= 1}
              onClick={() => setPagina((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="tabular-nums text-muted-foreground">
              Página {pagina} de {paginas}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagina >= paginas}
              onClick={() => setPagina((p) => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        )}
      </div>

      <FichaDeLaSolicitud
        id={abierta}
        onCerrar={() => setAbierta(null)}
        onCambio={() => void cargar()}
        avisar={toast}
      />
    </AppLayout>
  );
}

type Avisar = ReturnType<typeof useToast>['toast'];

async function copiar(texto: string, avisar: Avisar, que: string) {
  try {
    await navigator.clipboard.writeText(texto);
    avisar({ title: `${que} copiado` });
  } catch {
    avisar({
      variant: 'destructive',
      title: `No se pudo copiar el ${que.toLowerCase()}`,
      description: 'Cópialo a mano.',
    });
  }
}

/** El enlace completo de una invitación, con el sitio desde el que se mira el panel. */
const enlaceCompleto = (relativo: string) => `${window.location.origin}${relativo}`;

function Dato({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{titulo}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

export function FichaDeLaSolicitud({
  id,
  onCerrar,
  onCambio,
  avisar,
}: {
  id: string | null;
  onCerrar: () => void;
  onCambio: () => void;
  avisar: Avisar;
}) {
  const [ficha, setFicha] = useState<FichaDeSolicitud | null>(null);
  const [invitacion, setInvitacion] = useState<InvitacionDeLaSolicitud | null>(null);
  const [nota, setNota] = useState('');
  const [motivo, setMotivo] = useState('');
  const [descartando, setDescartando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    setFicha(null);
    setInvitacion(null);
    setDescartando(false);
    setMotivo('');
    if (!id) return;
    let vigente = true;
    solicitudesService
      .ficha(id)
      .then(async (f) => {
        // §5.4: al abrir una NUEVA, el panel la marca EN_REVISION.
        // Si no se puede marcar (p. ej. soporte, que sólo mira), se muestra igual.
        const vista =
          f.estado === 'NUEVA'
            ? await solicitudesService.cambiar(id, { estado: 'EN_REVISION' }).catch(() => f)
            : f;
        if (!vigente) return;
        if (f.estado === 'NUEVA') onCambio();
        setFicha(vista);
        setNota(vista.notaInterna ?? '');
      })
      .catch((e) => {
        if (vigente) {
          avisar({
            variant: 'destructive',
            title: 'No se pudo abrir la solicitud',
            description: mensajeDeError(e, 'Prueba de nuevo.'),
          });
        }
      });
    return () => {
      vigente = false;
    };
    // `onCambio` y `avisar` cambian en cada render del padre: sólo importa la solicitud.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const hacer = async (accion: () => Promise<FichaDeSolicitud>, listo: string) => {
    setTrabajando(true);
    try {
      const f = await accion();
      setFicha(f);
      setNota(f.notaInterna ?? '');
      onCambio();
      avisar({ title: listo });
      return true;
    } catch (e) {
      avisar({
        variant: 'destructive',
        title: 'No se pudo guardar',
        description: mensajeDeError(e, 'Prueba de nuevo.'),
      });
      return false;
    } finally {
      setTrabajando(false);
    }
  };

  const invitar = async (preguntar: boolean) => {
    if (!ficha) return;
    if (
      preguntar &&
      !(await confirmar({
        title: 'Crear invitación',
        description: `${ficha.nombre} recibe un enlace para UNA sola empresa. El enlace vence en 14 días si no lo usa; sus 15 días de Mini Prixia empiezan cuando entra.`,
        confirmText: 'Crear invitación',
      }))
    ) {
      return;
    }
    setTrabajando(true);
    try {
      const r = await solicitudesService.invitar(ficha.id);
      setInvitacion(r);
      setFicha(r.solicitud);
      onCambio();
      if (preguntar) avisar({ title: 'Invitación creada' });
    } catch (e) {
      avisar({
        variant: 'destructive',
        title: 'No se pudo crear la invitación',
        description: mensajeDeError(e, 'Prueba de nuevo.'),
      });
    } finally {
      setTrabajando(false);
    }
  };

  // Una invitada que se abre de nuevo: el enlace y el correo sugerido se vuelven a pedir (es idempotente).
  useEffect(() => {
    if (ficha?.invitacion && !invitacion && !ficha.datosBorrados) void invitar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ficha?.id, ficha?.invitacion?.id]);

  const borrar = async () => {
    if (!ficha) return;
    const ok = await confirmar({
      title: 'Borrar datos',
      description:
        'Se borran el nombre, el correo, el teléfono, la empresa, el mensaje y la nota. Queda sólo la fecha, el rubro y el estado. No se puede deshacer.',
      confirmText: 'Borrar datos',
      variant: 'destructive',
    });
    if (ok) await hacer(() => solicitudesService.borrarDatos(ficha.id), 'Datos borrados');
  };

  const correo = invitacion?.correoSugerido;
  const mailto =
    ficha?.correo && correo
      ? `mailto:${ficha.correo}?subject=${encodeURIComponent(correo.asunto)}&body=${encodeURIComponent(correo.cuerpo)}`
      : null;

  return (
    <Sheet open={Boolean(id)} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>
            {ficha ? (ficha.datosBorrados ? 'Datos borrados' : ficha.nombre) : 'Solicitud'}
          </SheetTitle>
          <SheetDescription>
            {ficha ? `${ficha.empresa ?? '—'} · llegó el ${fecha(ficha.createdAt)}` : 'Cargando…'}
          </SheetDescription>
        </SheetHeader>

        {!ficha ? (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-6" />
            <Skeleton className="h-24" />
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <PastillaDeEstado estado={ficha.estado} />
              {ficha.vecesPedida > 1 && (
                <span className="text-xs text-muted-foreground">
                  Pidió {ficha.vecesPedida} veces
                </span>
              )}
            </div>

            <dl className="grid grid-cols-2 gap-4">
              <Dato titulo="Correo">
                {ficha.correo ?? '—'}
                {ficha.correoPersonal && (
                  <Badge variant="outline" className="ml-2">
                    Correo personal
                  </Badge>
                )}
              </Dato>
              <Dato titulo="Teléfono o WhatsApp">{ficha.telefono ?? '—'}</Dato>
              <Dato titulo="Cargo">{ficha.cargo ?? '—'}</Dato>
              <Dato titulo="Rubro">{rotuloDeRubro(ficha.rubro) ?? '—'}</Dato>
              <Dato titulo="Catálogo">{rotuloDeTamano(ficha.tamanoDeCatalogo) ?? '—'}</Dato>
              <Dato titulo="Asunto">
                {ficha.asunto ? rotuloDeAsunto(ficha.asunto) : 'Pedir su Mini Prixia'}
              </Dato>
            </dl>

            {ficha.queQuiereResolver && (
              <div>
                <p className="text-xs text-muted-foreground">Qué quiere resolver</p>
                <p className="mt-1 whitespace-pre-wrap text-sm">{ficha.queQuiereResolver}</p>
              </div>
            )}

            {ficha.estado === 'DESCARTADA' && ficha.motivoDescarte && (
              <p className="text-sm">
                <span className="text-muted-foreground">Motivo del descarte: </span>
                {ficha.motivoDescarte}
              </p>
            )}

            {ficha.alta && (
              <div className="rounded-md border border-ok-borde bg-ok-suave p-3 text-sm">
                Entró el {fecha(ficha.alta.createdAt)} y armó «{ficha.alta.empresa}».
                {!ficha.alta.empresaExiste && ' La empresa ya no existe.'}
              </div>
            )}

            {ficha.invitacion && (
              <div className="space-y-2 rounded-md border border-border p-3">
                <p className="text-sm">
                  <Ticket className="mr-1.5 inline h-4 w-4" aria-hidden="true" />
                  Invitación <code className="text-xs">{ficha.invitacion.codigo}</code>
                  {ficha.invitacion.venceEn && ficha.invitacion.altasUsadas === 0
                    ? ` · vence el ${fecha(ficha.invitacion.venceEn)}`
                    : ''}
                </p>
                {ficha.invitacion.altasUsadas === 0 && !ficha.datosBorrados && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        void copiar(enlaceCompleto(ficha.invitacion!.enlace), avisar, 'Enlace')
                      }
                    >
                      <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                      Copiar enlace
                    </Button>
                    {correo && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          void copiar(
                            `${correo.cuerpo}`.replace(
                              /\S*\/invitacion\/\S+/,
                              enlaceCompleto(ficha.invitacion!.enlace)
                            ),
                            avisar,
                            'Correo'
                          )
                        }
                      >
                        <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                        Copiar correo sugerido
                      </Button>
                    )}
                    {mailto && (
                      <a
                        href={mailto}
                        className={buttonVariants({ variant: 'outline', size: 'sm' })}
                      >
                        <Mail className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                        Abrir en tu correo
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

            {correo && ficha.invitacion?.altasUsadas === 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Correo sugerido</p>
                <pre className="mt-1 whitespace-pre-wrap rounded-md bg-muted p-3 font-sans text-sm">
                  {correo.cuerpo.replace(
                    /\S*\/invitacion\/\S+/,
                    enlaceCompleto(ficha.invitacion.enlace)
                  )}
                </pre>
              </div>
            )}

            {!ficha.datosBorrados && (
              <div className="space-y-2">
                <label htmlFor="nota-interna" className="text-xs text-muted-foreground">
                  Nota interna (sólo la ve la plataforma)
                </label>
                <Textarea
                  id="nota-interna"
                  value={nota}
                  maxLength={2000}
                  onChange={(e) => setNota(e.target.value)}
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={trabajando || nota === (ficha.notaInterna ?? '')}
                  onClick={() =>
                    void hacer(
                      () => solicitudesService.cambiar(ficha.id, { notaInterna: nota }),
                      'Nota guardada'
                    )
                  }
                >
                  Guardar nota
                </Button>
              </div>
            )}

            {descartando && (
              <div className="space-y-2">
                <label htmlFor="motivo-descarte" className="text-xs text-muted-foreground">
                  ¿Por qué la descartas?
                </label>
                <Input
                  id="motivo-descarte"
                  value={motivo}
                  maxLength={300}
                  placeholder="Spam, duplicada, no califica…"
                  onChange={(e) => setMotivo(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={trabajando || !motivo.trim()}
                    onClick={async () => {
                      const ok = await hacer(
                        () =>
                          solicitudesService.cambiar(ficha.id, {
                            estado: 'DESCARTADA',
                            motivoDescarte: motivo.trim(),
                          }),
                        'Solicitud descartada'
                      );
                      if (ok) setDescartando(false);
                    }}
                  >
                    Descartar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDescartando(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            <div className={cn('flex flex-wrap gap-2 border-t border-border pt-4')}>
              {!ficha.invitacion && !ficha.datosBorrados && ficha.estado !== 'DESCARTADA' && (
                <Button disabled={trabajando} onClick={() => void invitar(true)}>
                  <Ticket className="mr-2 h-4 w-4" aria-hidden="true" />
                  Crear invitación
                </Button>
              )}
              {!ficha.invitacion && ficha.estado !== 'DESCARTADA' && !descartando && (
                <Button
                  variant="outline"
                  disabled={trabajando}
                  onClick={() => setDescartando(true)}
                >
                  <XCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                  Descartar
                </Button>
              )}
              {ficha.estado === 'DESCARTADA' && (
                <Button
                  variant="outline"
                  disabled={trabajando}
                  onClick={() =>
                    void hacer(
                      () => solicitudesService.cambiar(ficha.id, { estado: 'EN_REVISION' }),
                      'Solicitud abierta de nuevo'
                    )
                  }
                >
                  <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
                  Volver a abrir
                </Button>
              )}
              {!ficha.datosBorrados && (
                <Button
                  variant="ghost"
                  className="text-destructive"
                  disabled={trabajando}
                  onClick={() => void borrar()}
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  Borrar datos
                </Button>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default SolicitudesPage;
